import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Async thunk for scheduling a message
export const scheduleMessage = createAsyncThunk(
  "scheduledMessages/scheduleMessage",
  async (data, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("No token found");
      }

      const formData = new FormData();
      formData.append("contactId", data.contactId);
      formData.append("messageType", data.messageType);
      formData.append("scheduledTime", data.scheduledTime);
      
      if (data.messageType === "text") {
        formData.append("messageContent", data.messageContent);
      } else if (data.messageType === "template") {
        // For template messages, send as JSON
        formData.append("templateData", JSON.stringify(data.templateData));
        if (data.templateCustomizations) {
          formData.append("templateCustomizations", JSON.stringify(data.templateCustomizations));
        }
      } else if (data.mediaFile) {
        formData.append("mediaFile", data.mediaFile);
        if (data.mediaFileName) {
          formData.append("mediaFileName", data.mediaFileName);
        }
      }

      if (data.originSocketId) {
        formData.append("originSocketId", data.originSocketId);
      }

      const response = await axios.post(
        `${SERVER_DOMAIN}/api/scheduled-messages/schedule`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to schedule message"
      );
    }
  }
);

// Async thunk for fetching scheduled messages
export const fetchScheduledMessages = createAsyncThunk(
  "scheduledMessages/fetchScheduledMessages",
  async (contactId, { rejectWithValue }) => {
    console.log('📡 fetchScheduledMessages thunk called with contactId:', contactId);
    try {
      const token = await getAuthToken();
      if (!token) {
        console.error('❌ No token found');
        return rejectWithValue("No token found");
      }

      const url = `${SERVER_DOMAIN}/api/scheduled-messages/contact/${contactId}`;
      console.log('🌐 Making API request to:', url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('📥 API Response:', {
        status: response.status,
        data: response.data,
        messagesCount: response.data?.data?.length || 0,
      });

      return response.data.data || [];
    } catch (error) {
      console.error('❌ Error in fetchScheduledMessages:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch scheduled messages"
      );
    }
  }
);

// Async thunk for cancelling a scheduled message
export const cancelScheduledMessage = createAsyncThunk(
  "scheduledMessages/cancelScheduledMessage",
  async (scheduledMessageId, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("No token found");
      }

      const response = await axios.delete(
        `${SERVER_DOMAIN}/api/scheduled-messages/${scheduledMessageId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return { scheduledMessageId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel scheduled message"
      );
    }
  }
);

const scheduledMessageSlice = createSlice({
  name: "scheduledMessages",
  initialState: {
    scheduledMessages: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addScheduledMessage: (state, action) => {
      state.scheduledMessages.push(action.payload);
    },
    removeScheduledMessage: (state, action) => {
      state.scheduledMessages = state.scheduledMessages.filter(
        (msg) => msg._id !== action.payload
      );
    },
    updateScheduledMessage: (state, action) => {
      const index = state.scheduledMessages.findIndex(
        (msg) => msg._id === action.payload._id
      );
      if (index !== -1) {
        state.scheduledMessages[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Schedule message
    builder
      .addCase(scheduleMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(scheduleMessage.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          state.scheduledMessages.push(action.payload.data);
        }
      })
      .addCase(scheduleMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch scheduled messages
      .addCase(fetchScheduledMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScheduledMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.scheduledMessages = action.payload;
      })
      .addCase(fetchScheduledMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Cancel scheduled message
      .addCase(cancelScheduledMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelScheduledMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.scheduledMessages = state.scheduledMessages.filter(
          (msg) => msg._id !== action.payload.scheduledMessageId
        );
      })
      .addCase(cancelScheduledMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  addScheduledMessage,
  removeScheduledMessage,
  updateScheduledMessage,
} = scheduledMessageSlice.actions;

export default scheduledMessageSlice.reducer;

